import { useState } from 'react'
import { useNavigate } from 'react-router'

import { Avatar, Backdrop } from '@/components/primitives'
import { useResellerMe } from '@/features/reseller/api'
import { useMyWorkspaces, useSwitchWorkspace } from '@/features/workspaces/queries'
import { usePlatformAdmin } from '@/lib/auth/platform'
import { ROLE_LABELS } from '@/lib/permissions/catalog'
import { cn } from '@/lib/utils/cn'

export function WorkspaceSwitcher({
  currentSlug,
  onSelect,
  framed = false,
}: {
  currentSlug: string
  /** Draw it as a card — used where nothing else frames it. */
  framed?: boolean
  /** Pick a workspace without navigating into it (the reseller panel). */
  onSelect?: ((slug: string) => void) | undefined
}) {
  const [open, setOpen] = useState(false)
  const { data: workspaces } = useMyWorkspaces()
  const switchTo = useSwitchWorkspace()
  const navigate = useNavigate()
  const isReseller = Boolean(useResellerMe().data)
  const isPlatformAdmin = usePlatformAdmin().isSuccess

  const current = workspaces?.find((w) => w.slug === currentSlug)
  if (!current) return null

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
        className={cn(
          'flex h-[44px] w-full items-center gap-2 rounded-control px-2',
          'hover:bg-surface-hover transition-colors duration-control',
          framed && 'border border-border bg-surface-sunken shadow-resting',
        )}
      >
        <Avatar name={current.name} src={current.logo_url} size="sm" />
        <span className="min-w-0 flex-1 truncate text-left text-compact font-medium">
          {current.name}
        </span>
        <span aria-hidden="true" className="text-text-tertiary">
          ⌄
        </span>
      </button>

      {open && (
        <>
          {/* Click-away. Presentational — Escape and the toggle also close. */}
          <Backdrop onClick={() => setOpen(false)} dim={false} label="Close workspace menu" />
          <div
            role="menu"
            data-state="open"
            className={cn(
              'panel absolute top-full left-0 z-50 mt-1 w-64',
              'rounded-card border border-border bg-surface-raised p-1 shadow-lifted',
            )}
          >
            {workspaces?.map((workspace) => (
              <button
                key={workspace.id}
                role="menuitem"
                type="button"
                onClick={() => {
                  setOpen(false)
                  if (workspace.slug === currentSlug) return
                  if (onSelect) onSelect(workspace.slug)
                  else switchTo(workspace.slug)
                }}
                className={cn(
                  'flex w-full items-center gap-3 rounded-control p-2 text-left',
                  'hover:bg-surface-hover',
                  workspace.slug === currentSlug && 'bg-surface-selected',
                )}
              >
                <Avatar name={workspace.name} src={workspace.logo_url} size="sm" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-compact font-medium">
                    {workspace.name}
                  </span>
                  <span className="block text-caption text-text-tertiary">
                    {ROLE_LABELS[workspace.role] ?? workspace.role}
                  </span>
                </span>
                {workspace.slug === currentSlug && (
                  <span aria-hidden="true" className="text-action">
                    ✓
                  </span>
                )}
              </button>
            ))}

            <div className="my-1 border-t border-border" />

            {isReseller && (
              <button
                role="menuitem"
                type="button"
                onClick={() => {
                  setOpen(false)
                  void navigate('/reseller')
                }}
                className="w-full rounded-control p-2 text-left text-compact text-text hover:bg-surface-hover"
              >
                Reseller panel
              </button>
            )}
            {isPlatformAdmin && (
              <button
                role="menuitem"
                type="button"
                onClick={() => {
                  setOpen(false)
                  void navigate('/admin')
                }}
                className="w-full rounded-control p-2 text-left text-compact text-text hover:bg-surface-hover"
              >
                Super admin
              </button>
            )}

            <button
              role="menuitem"
              type="button"
              onClick={() => {
                setOpen(false)
                void navigate('/onboarding')
              }}
              className="w-full rounded-control p-2 text-left text-compact text-action hover:bg-surface-hover"
            >
              Create a workspace
            </button>
          </div>
        </>
      )}
    </div>
  )
}
